import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { AssetCondition, AssetFacet, AssetUsage } from "../api/assets";
import type { PositionNode } from "../api/structure";
import { fieldTitle, formatNumberSr, rangeProblem, type FilterState, type SearchField } from "../lib/assetSearch";
import { formatDate } from "../lib/dates";
import type { SelectOption } from "./Select";
import { Select } from "./Select";
import { DateInput } from "./DateInput";
import { Modal } from "./Modal";
import { PositionTreePicker } from "./PositionTreePicker";
import { conditionLabel, usageLabel } from "./ConditionChip";
import Ic from "./icons.jsx";

/** Up to this many choices are shown as toggles; a longer list becomes a searchable dropdown. */
const TOGGLE_LIMIT = 8;

type Translate = (key: string, fallback: string, opts?: Record<string, unknown>) => string;

type Props = {
  field: SearchField;
  filter: FilterState;
  /** Choices of this field given every other criterion; undefined until the first answer arrives. */
  facet: AssetFacet | undefined;
  onChange: (next: FilterState) => void;
  onRemove: () => void;
  /** Options of the physical-location picker (indented tree), for the `locationId` row. */
  locationOptions: SelectOption<string>[];
};

/**
 * One criterion of the advanced search (sprint 5i): the field's name, an editor
 * that fits how the field is matched — toggles or a dropdown for a list of
 * values, "od–do" for numbers and dates, a text box, a tree picker — and the
 * remove button. Every choice carries how many assets it would leave, counted
 * with the other criteria applied.
 */
export function AssetFilterRow({ field, filter, facet, onChange, onRemove, locationOptions }: Props) {
  const { t } = useTranslation();
  const title = fieldTitle(field);

  return (
    <div className="filter-row" role="group" aria-label={title}>
      <div className="filter-row-label">{title}</div>
      <div className="filter-row-body">
        {field.kind === "values" && <ValuesEditor t={t} field={field} filter={filter} facet={facet} onChange={onChange} />}
        {field.kind === "text" && <TextEditor t={t} field={field} filter={filter} facet={facet} onChange={onChange} />}
        {(field.kind === "number" || field.kind === "date") && <RangeEditor t={t} field={field} filter={filter} facet={facet} onChange={onChange} />}
        {field.kind === "tree" && field.name === "positionId" && <PositionEditor t={t} filter={filter} onChange={onChange} />}
        {field.kind === "tree" && field.name === "locationId" && (
          <Select<string>
            value={filter.values?.[0] ?? ""}
            options={locationOptions}
            onChange={(id) => onChange({ field: filter.field, values: id ? [id] : [] })}
            placeholder={t("advSearch.pickLocation", "Izaberi lokaciju…")}
            ariaLabel={title}
            style={{ flex: "1 1 260px", maxWidth: 420, minWidth: 0 }}
          />
        )}
      </div>
      <button type="button" className="icon-btn filter-row-remove" onClick={onRemove} aria-label={t("advSearch.removeFilter", "Ukloni filter {{name}}", { name: title })} title={t("advSearch.remove", "Ukloni filter")}>
        <Ic.x />
      </button>
    </div>
  );
}

type EditorProps = {
  t: Translate;
  field: SearchField;
  filter: FilterState;
  facet: AssetFacet | undefined;
  onChange: (next: FilterState) => void;
};

/** Display text of one value of a "values" field. */
function valueLabel(t: Translate, field: SearchField, value: string, facet: AssetFacet | undefined, filter: FilterState): string {
  switch (field.name) {
    case "condition": return conditionLabel(t, value as AssetCondition);
    case "usage": return usageLabel(t, value as AssetUsage);
    case "replacementKind": return value === "TEMPORARY" ? t("assets.replacementKind.TEMPORARY", "Privremena") : t("assets.replacementKind.PERMANENT", "Trajna");
    case "customMade": return value === "true" ? t("common.yes", "Da") : t("common.no", "Ne");
    case "typeId":
    case "manufacturerId":
    case "supplierId":
      return facet?.options.find((o) => o.value === value)?.label ?? filter.labels?.[value] ?? "…";
    default:
      if (field.attribute?.dataType === "BOOLEAN") return value === "true" ? t("common.yes", "Da") : t("common.no", "Ne");
      return value;
  }
}

function EmptyToggle({ t, field, filter, facet, onChange }: EditorProps) {
  if (!field.nullable) return null;
  const on = !!filter.empty;
  return (
    <button type="button" className={`chip${on ? " active" : ""}`} aria-pressed={on} onClick={() => onChange({ ...filter, empty: !on })}>
      {t("advSearch.empty", "Nije popunjeno")}
      {facet && <span className="chip-n">{facet.emptyCount}</span>}
    </button>
  );
}

function ValuesEditor(props: EditorProps) {
  const { t, field, filter, facet, onChange } = props;
  const selected = filter.values ?? [];

  // Fixed lists keep every value in place (a count of 0 instead of a vanishing toggle);
  // open lists show what exists, plus whatever is already picked.
  const base = field.fixedValues ?? (facet?.options.map((o) => o.value) ?? []);
  const values = [...base, ...selected.filter((v) => !base.includes(v))];

  const count = (v: string) => facet?.options.find((o) => o.value === v)?.count ?? 0;
  const label = (v: string) => valueLabel(t, field, v, facet, filter);

  function toggle(v: string) {
    const on = selected.includes(v);
    const next = on ? selected.filter((x) => x !== v) : [...selected, v];
    // Remember the name of a picked row: a link to this search shows it without a lookup.
    const labels = { ...(filter.labels ?? {}) };
    if (on) delete labels[v]; else labels[v] = label(v);
    onChange({ ...filter, values: next, labels });
  }

  const asToggles = !!field.fixedValues || values.length <= TOGGLE_LIMIT;

  if (asToggles) {
    return (
      <>
        {values.map((v) => {
          const on = selected.includes(v);
          const n = count(v);
          return (
            <button key={v} type="button" className={`chip${on ? " active" : ""}${facet && n === 0 && !on ? " is-zero" : ""}`} aria-pressed={on} onClick={() => toggle(v)}>
              {label(v)}
              {facet && <span className="chip-n">{n}</span>}
            </button>
          );
        })}
        {!facet && values.length === 0 && <span className="filter-row-hint">{t("common.loading", "Učitavanje…")}</span>}
        {facet && values.length === 0 && !field.nullable && <span className="filter-row-hint">{t("advSearch.noValues", "Nema vrednosti u ovom izboru.")}</span>}
        <EmptyToggle {...props} />
      </>
    );
  }

  const remaining: SelectOption<string>[] = values
    .filter((v) => !selected.includes(v))
    .map((v) => ({ value: v, label: `${label(v)} · ${count(v)}` }));
  return (
    <>
      {selected.map((v) => (
        <button key={v} type="button" className="chip active" onClick={() => toggle(v)} title={t("advSearch.removeValue", "Ukloni")}>
          {label(v)} <Ic.x aria-hidden="true" />
        </button>
      ))}
      <Select<string>
        value=""
        options={remaining}
        onChange={(v) => { if (v) toggle(v); }}
        placeholder={selected.length === 0 ? t("advSearch.pickValue", "Izaberi…") : t("advSearch.addValue", "Dodaj još…")}
        ariaLabel={fieldTitle(field)}
        style={{ flex: "1 1 220px", maxWidth: 340, minWidth: 0 }}
      />
      <EmptyToggle {...props} />
    </>
  );
}

function TextEditor(props: EditorProps) {
  const { t, field, filter, onChange } = props;
  return (
    <>
      <input
        className="input"
        type="search"
        value={filter.text ?? ""}
        onChange={(e) => onChange({ ...filter, text: e.target.value })}
        placeholder={t("advSearch.contains", "sadrži…")}
        aria-label={fieldTitle(field)}
        disabled={!!filter.empty}
        style={{ flex: "1 1 220px", maxWidth: 340, minWidth: 0 }}
      />
      <EmptyToggle {...props} />
    </>
  );
}

function RangeEditor(props: EditorProps) {
  const { t, field, filter, facet, onChange } = props;
  const title = fieldTitle(field);
  const isDate = field.kind === "date";
  const show = (v: string) => (isDate ? formatDate(v) : formatNumberSr(v));
  const hint = facet?.min != null && facet.max != null
    ? t("advSearch.rangeHint", "u izboru: {{min}} – {{max}}", { min: show(facet.min), max: show(facet.max) }) + (field.unit ? ` ${field.unit}` : "")
    : facet
      ? t("advSearch.rangeNone", "nema vrednosti u ovom izboru")
      : "";
  const disabled = !!filter.empty;
  // Half-typed ranges are not searched (see rangeProblem); say why, where the hint normally is.
  const problem = rangeProblem(field.kind, filter);
  const problemText = problem === "number"
    ? t("advSearch.rangeNotNumber", "Unesi broj, npr. 7,5.")
    : problem === "order"
      ? t("advSearch.rangeOrder", "„do“ je manje od „od“ — ispravi raspon.")
      : null;

  return (
    <>
      <div className="filter-range">
        {/* Each word stays with its box when the row wraps on a phone. */}
        <span className="filter-range-end">
          <span className="filter-range-word">{t("advSearch.from", "od")}</span>
          {isDate ? (
            <DateInput value={filter.from ?? ""} onChange={(v) => onChange({ ...filter, from: v })} disabled={disabled} ariaLabel={`${title} ${t("advSearch.from", "od")}`} />
          ) : (
            <input className="input" inputMode="decimal" value={filter.from ?? ""} onChange={(e) => onChange({ ...filter, from: e.target.value })} disabled={disabled} aria-label={`${title} ${t("advSearch.from", "od")}`} placeholder={facet?.min != null ? show(facet.min) : ""} />
          )}
        </span>
        <span className="filter-range-end">
          <span className="filter-range-word">{t("advSearch.to", "do")}</span>
          {isDate ? (
            <DateInput value={filter.to ?? ""} onChange={(v) => onChange({ ...filter, to: v })} disabled={disabled} ariaLabel={`${title} ${t("advSearch.to", "do")}`} />
          ) : (
            <input className="input" inputMode="decimal" value={filter.to ?? ""} onChange={(e) => onChange({ ...filter, to: e.target.value })} disabled={disabled} aria-label={`${title} ${t("advSearch.to", "do")}`} placeholder={facet?.max != null ? show(facet.max) : ""} />
          )}
        </span>
      </div>
      <span className={`filter-row-hint${problemText ? " is-err" : ""}`} role={problemText ? "alert" : undefined}>{problemText ?? hint}</span>
      <EmptyToggle {...props} />
    </>
  );
}

/** A position and everything below it, picked from the tree in a dialog. */
function PositionEditor({ t, filter, onChange }: { t: Translate; filter: FilterState; onChange: (next: FilterState) => void }) {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<PositionNode | null>(null);
  const id = filter.values?.[0] ?? null;
  const name = id ? filter.labels?.[id] ?? "…" : null;

  function confirm() {
    if (!picked) return;
    onChange({ field: filter.field, values: [picked.id], labels: { [picked.id]: picked.name } });
    setOpen(false);
  }

  return (
    <>
      {name && <span className="tag info">{t("advSearch.positionAndBelow", "{{name}} i sve ispod", { name })}</span>}
      <button type="button" className="btn sm" onClick={() => { setPicked(null); setOpen(true); }}>
        <Ic.tree /> {id ? t("advSearch.changePosition", "Promeni poziciju") : t("advSearch.pickPosition", "Izaberi poziciju")}
      </button>
      {open && (
        <Modal title={t("advSearch.positionTitle", "Filter po logičkoj poziciji")} subtitle={t("advSearch.positionSub", "Pretraga uzima izabranu poziciju i sve pozicije ispod nje.")} onClose={() => setOpen(false)} width={640}>
          <PositionTreePicker value={picked?.id ?? id} onChange={setPicked} />
          <div className="form-actions">
            <button type="button" className="btn lg" onClick={() => setOpen(false)}>{t("action.cancel", "Otkaži")}</button>
            <button type="button" className="btn primary lg" onClick={confirm} disabled={!picked}>{t("advSearch.pick", "Izaberi")}</button>
          </div>
        </Modal>
      )}
    </>
  );
}
